/**
 * GritClaw v2 client — talks to the GritClaw gateway (a different host from the
 * orchestrator `axiosInstance`, so this uses its own fetch wrapper). Implements the
 * AG-UI streaming protocol (POST + SSE, which EventSource cannot do), the wake-on-open
 * endpoint, the user-scoped cron API, and APNs-style device registration.
 *
 * Contract: grit-claw-v2/contracts/app-channel.md.
 */
import env from 'react-dotenv';
import { Cookies } from 'react-cookie';

import type { A2uiPayload } from '../../components/GritClaw/a2ui/types';
import type { ScreenContext } from '../../context/GritClaw/screenContext';
import { expireSessionAndRedirect } from '../../utils/sessionExpiry';
import { refreshSession } from '../../utils/sessionRefresh';

const cookies = new Cookies();

export const gritClawBaseUrl = (): string =>
  (env.REACT_APP_GRITCLAW_URL || 'http://localhost:8088').replace(/\/$/, '');

const authHeader = (): Record<string, string> => {
  const token = cookies.get('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Impersonation sessions (super-user or reseller acting as a client) carry a
 *  token the GritClaw gateway's membership revalidation can never accept —
 *  a SuperUser has no membership row, a reseller has none in the client
 *  company. Its 401/403 means "no assistant here", not a dead session. */
export const isImpersonatingSession = (): boolean => {
  const user = cookies.get('user') as { company_id?: string } | undefined;
  return (
    (!!cookies.get('super_user') && !!user?.company_id) ||
    !!cookies.get('original_company_id')
  );
};

const uuid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const fallbackThreadByKey = new Map<string, string>();
const fallbackConversationByKey = new Map<string, string>();

export const DEFAULT_GRITCLAW_CONVERSATION_ID = 'main';

const principalForThread = (): string => {
  const user = cookies.get('user') as
    | { id?: string; email?: string; company_id?: string }
    | undefined;
  return String(
    user?.id || user?.email || user?.company_id || 'anonymous'
  ).replace(/[^a-zA-Z0-9_.:-]/g, '_');
};

const threadStorageKey = (): string =>
  `gritclaw.agui.threadId.v1.${principalForThread()}`;
const conversationStorageKey = (): string =>
  `gritclaw.agui.conversationId.v1.${principalForThread()}`;

export const normalizeGritClawConversationId = (id?: string | null): string => {
  const trimmed = String(id ?? '').trim();
  return trimmed || DEFAULT_GRITCLAW_CONVERSATION_ID;
};

export const getOrCreateGritClawThreadId = (): string => {
  const key = threadStorageKey();
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const next = uuid();
    window.localStorage.setItem(key, next);
    return next;
  } catch {
    const existing = fallbackThreadByKey.get(key);
    if (existing) return existing;
    const next = uuid();
    fallbackThreadByKey.set(key, next);
    return next;
  }
};

export const resetGritClawThreadId = (): string => {
  const key = threadStorageKey();
  const next = uuid();
  fallbackThreadByKey.set(key, next);
  try {
    window.localStorage.setItem(key, next);
  } catch {
    /* localStorage can be unavailable in private contexts; in-memory fallback is enough. */
  }
  return next;
};

export const getStoredGritClawConversationId = (): string => {
  const key = conversationStorageKey();
  try {
    return normalizeGritClawConversationId(window.localStorage.getItem(key));
  } catch {
    return normalizeGritClawConversationId(fallbackConversationByKey.get(key));
  }
};

export const setStoredGritClawConversationId = (
  conversationId: string
): string => {
  const key = conversationStorageKey();
  const normalized = normalizeGritClawConversationId(conversationId);
  fallbackConversationByKey.set(key, normalized);
  try {
    window.localStorage.setItem(key, normalized);
  } catch {
    /* localStorage can be unavailable in private contexts; in-memory fallback is enough. */
  }
  return normalized;
};

export const createGritClawConversationId = (): string => uuid();
export const createGritClawRunId = (): string => uuid();

const DEFAULT_REQUEST_TIMEOUT_MS = 30000;
const LONG_REQUEST_TIMEOUT_MS = 60000;
const sessionRejectedResponses = new WeakSet<Response>();

const requestTimeoutMs = (fallback = DEFAULT_REQUEST_TIMEOUT_MS): number => {
  const configured = Number(env.REACT_APP_API_TIMEOUT_MS);
  return Number.isFinite(configured) && configured > 0 ? configured : fallback;
};

const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = requestTimeoutMs()
): Promise<Response> => {
  const runFetch = async (withFreshAuth: boolean): Promise<Response> => {
    const merged = withFreshAuth
      ? {
          ...init,
          headers: {
            ...(init.headers as Record<string, string> | undefined),
            ...authHeader(),
          },
        }
      : init;
    if (merged.signal || timeoutMs <= 0) {
      return fetch(input, merged);
    }
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(input, { ...merged, signal: controller.signal });
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const handleSessionState = async (
    response: Response,
    canRetry: boolean
  ): Promise<Response> => {
    // An expired access token gets the same treatment as on the orchestrator
    // axios instance and the SSE hook: one silent refresh + one retry. Only a
    // failed rotation (or a 401 on the retried request) ends the session.
    if (response.status === 401 && canRetry && !isImpersonatingSession()) {
      const refreshed = await refreshSession();
      if (refreshed) {
        return handleSessionState(await runFetch(true), false);
      }
    }
    let rejected = response.status === 401;
    if (!rejected && response.status === 403) {
      try {
        const readable =
          typeof response.clone === 'function' ? response.clone() : response;
        const body = (await readable.json()) as {
          detail?: string | { code?: string; message?: string };
        };
        const detail = body?.detail;
        rejected =
          (typeof detail === 'object' &&
            detail?.code === 'MEMBERSHIP_REVOKED') ||
          detail === 'platform membership is no longer active';
      } catch {
        /* A generic 403 is a permission failure, not a terminal session signal. */
      }
    }
    if (rejected && !isImpersonatingSession()) {
      sessionRejectedResponses.add(response);
      expireSessionAndRedirect();
    }
    return response;
  };

  return handleSessionState(await runFetch(false), true);
};

// ── AG-UI run ───────────────────────────────────────────────────────────────

export interface AgUiHandlers {
  onRunStarted?: (runId: string) => void;
  onTextDelta: (delta: string) => void;
  onUiBlock: (payload: A2uiPayload) => void;
  onProgress?: (label: string, phase: string) => void;
  onCorrelationId?: (corrId: string) => void;
  /** The HTTP stream ended without a terminal event. The server-side turn may
   *  still be running, so the provider reconciles it from durable history. */
  onDisconnected?: () => Promise<void>;
  onError: (message: string) => void;
  onFinished: () => void;
}

const customerSafeRunError = (raw: unknown): string => {
  const message = String(raw ?? '').trim();
  if (!message) return "GritClaw couldn't finish this response. Please retry.";
  const exposesInternals =
    /mcp__|tool[_ -]?(?:call|search|result|use)|(?:query|export|get|list)_[a-z0-9_]+|TOOL_CALL|RUN_(?:STARTED|FINISHED|ERROR)|\b(?:SQL|gRPC|FastAPI|SDK|stack trace|traceback)\b/i.test(
      message
    );
  if (exposesInternals || message.length > 320 || message.includes('\n')) {
    return "GritClaw couldn't finish this response. Please retry.";
  }
  return message;
};

const SAFE_PROGRESS_LABELS = new Set([
  'Working on your request',
  'Checking sensor history',
  'Checking device status',
  'Checking shipment details',
  'Checking alerts',
  'Preparing your file',
  'Preparing the result',
  'Reviewing results',
]);

const customerSafeProgressLabel = (raw: unknown): string => {
  const label = typeof raw === 'string' ? raw.trim() : '';
  return SAFE_PROGRESS_LABELS.has(label) ? label : 'Working on your request';
};

/**
 * Stream one assistant turn over AG-UI. Returns when the run finishes/errors or the
 * AbortSignal fires. Customer-visible UI arrives only through the gateway's
 * server-validated CUSTOM/a2ui event.
 */
export async function streamRun(
  text: string,
  screenContext: ScreenContext | null,
  handlers: AgUiHandlers,
  signal?: AbortSignal,
  threadId = getOrCreateGritClawThreadId(),
  conversationId = getStoredGritClawConversationId(),
  // Uploaded attachment ids (from uploadAttachment). Forwarded to the gateway so
  // the turn can reference the staged files; omitted from the wire when empty.
  attachments: string[] = [],
  runId = createGritClawRunId()
): Promise<void> {
  const normalizedConversationId =
    normalizeGritClawConversationId(conversationId);

  let response: Response;
  try {
    response = await fetchWithTimeout(`${gritClawBaseUrl()}/agui/run`, {
      method: 'POST',
      signal,
      headers: {
        ...authHeader(),
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({
        threadId,
        runId,
        state: screenContext ? { screen_context: screenContext } : {},
        messages: [{ id: uuid(), role: 'user', content: text }],
        tools: [],
        context: [],
        forwardedProps: {
          conversation_id: normalizedConversationId,
          ...(attachments.length ? { attachments } : {}),
        },
      }),
    });
  } catch (err) {
    handlers.onError(err instanceof Error ? err.message : 'network error');
    return;
  }

  if (!response.ok || !response.body) {
    handlers.onError(
      sessionRejectedResponses.has(response)
        ? 'Your session has expired — sign in again.'
        : `GritClaw is unavailable (${response.status}).`
    );
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let streamTerminal = false;

  const handleEvent = (raw: string): void => {
    const line = raw.split('\n').find((l) => l.startsWith('data:'));
    if (!line) return;
    let ev: Record<string, unknown>;
    try {
      ev = JSON.parse(line.slice(line.indexOf(':') + 1).trim());
    } catch {
      return;
    }
    switch (ev.type) {
      case 'RUN_STARTED':
        handlers.onRunStarted?.(String(ev.runId ?? runId));
        break;
      case 'TEXT_MESSAGE_CONTENT':
        if (typeof ev.delta === 'string') handlers.onTextDelta(ev.delta);
        break;
      case 'RUN_ERROR':
        handlers.onError(customerSafeRunError(ev.message));
        streamTerminal = true;
        break;
      case 'RUN_FINISHED':
        handlers.onFinished();
        streamTerminal = true;
        break;
      case 'CUSTOM': {
        const name = String(ev.name ?? '');
        if (name === 'a2ui' && ev.value && typeof ev.value === 'object') {
          handlers.onUiBlock(ev.value as A2uiPayload);
        } else if (
          name === 'progress' &&
          ev.value &&
          typeof ev.value === 'object'
        ) {
          const progress = ev.value as Record<string, unknown>;
          const label = customerSafeProgressLabel(progress.label);
          const phase =
            typeof progress.phase === 'string' ? progress.phase : 'work';
          handlers.onProgress?.(label, phase);
        } else if (name === 'corr_id' && typeof ev.value === 'string') {
          handlers.onCorrelationId?.(ev.value);
        }
        break;
      }
      case 'TEXT_MESSAGE_START':
      case 'TEXT_MESSAGE_END':
      case 'TOOL_CALL_START':
      case 'TOOL_CALL_ARGS':
      case 'TOOL_CALL_END':
      case 'TOOL_CALL_RESULT':
      case 'STATE_SNAPSHOT':
      case 'STATE_DELTA':
      case 'MESSAGES_SNAPSHOT':
      case 'REASONING_START':
      case 'REASONING_END':
      case 'REASONING_MESSAGE_START':
      case 'REASONING_MESSAGE_CONTENT':
      case 'REASONING_MESSAGE_END':
      case 'REASONING_ENCRYPTED_VALUE':
        break;
      default:
        break;
    }
  };

  let disconnected = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        handleEvent(frame);
        if (streamTerminal) break;
      }
      if (streamTerminal) {
        await reader.cancel();
        break;
      }
    }
    if (buffer.trim()) handleEvent(buffer);
    disconnected = !streamTerminal && !signal?.aborted;
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      disconnected = true;
    }
  } finally {
    reader.releaseLock();
  }
  if (disconnected) {
    if (handlers.onDisconnected) await handlers.onDisconnected();
    else
      handlers.onError(
        'The connection was interrupted. Reopen this conversation to see the completed response.'
      );
  }
}

/** Explicitly stop a run. Network disconnects alone do not cancel server work. */
export const cancelGritClawRun = async (runId: string): Promise<void> => {
  if (!runId) return;
  const response = await fetchWithTimeout(
    `${gritClawBaseUrl()}/agui/runs/${encodeURIComponent(runId)}/cancel`,
    { method: 'POST', headers: authHeader() }
  );
  if (!response.ok) {
    const error = new Error(`stop failed (${response.status})`) as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }
};

export interface GritClawDiagnosticsCapabilities {
  enabled: boolean;
  raw_enabled: boolean;
}

export interface GritClawDiagnosticStep {
  operation: string;
  status: 'ok' | 'error';
  attempt: number;
  duration_ms?: number | null;
  input_bytes?: number | null;
  output_bytes?: number | null;
  truncated: boolean;
  started_at: string;
  input?: unknown;
  result?: string | null;
}

export interface GritClawTurnDiagnostics {
  corr_id: string;
  status: string;
  started_at: string;
  duration_ms?: number | null;
  model?: string | null;
  steps: GritClawDiagnosticStep[];
  raw: boolean;
  raw_available: boolean;
  trace_url?: string | null;
}

export const fetchDiagnosticsCapabilities =
  async (): Promise<GritClawDiagnosticsCapabilities> => {
    const response = await fetchWithTimeout(
      `${gritClawBaseUrl()}/app/diagnostics/capabilities`,
      { headers: authHeader() }
    );
    if (!response.ok) return { enabled: false, raw_enabled: false };
    return response.json() as Promise<GritClawDiagnosticsCapabilities>;
  };

export const fetchTurnDiagnostics = async (
  corrId: string,
  raw = false
): Promise<GritClawTurnDiagnostics> => {
  const response = await fetchWithTimeout(
    `${gritClawBaseUrl()}/app/diagnostics/turns/${encodeURIComponent(
      corrId
    )}?raw=${raw}`,
    { headers: authHeader() }
  );
  if (!response.ok)
    throw new Error(`diagnostics unavailable (${response.status})`);
  return response.json() as Promise<GritClawTurnDiagnostics>;
};

export const captureNextDiagnosticTurn = async (): Promise<void> => {
  const response = await fetchWithTimeout(
    `${gritClawBaseUrl()}/app/diagnostics/capture-next`,
    { method: 'POST', headers: authHeader() }
  );
  if (!response.ok) throw new Error(`capture unavailable (${response.status})`);
};

// ── Report downloads (GET /app/reports/{id}) ──────────────────────────────────

/**
 * Download a generated PDF report. The `file_download` A2UI block carries only an
 * opaque `report_id`; resolve it here with the user's JWT — a plain `<a href>`
 * can't send the Authorization header — then save the returned blob. Throws on a
 * non-2xx response so the card can surface an error.
 */
export const downloadReport = async (
  reportId: string,
  filename: string
): Promise<void> => {
  const res = await fetchWithTimeout(
    `${gritClawBaseUrl()}/app/reports/${encodeURIComponent(reportId)}`,
    { headers: authHeader() },
    requestTimeoutMs(LONG_REQUEST_TIMEOUT_MS)
  );
  if (!res.ok) {
    throw new Error(
      res.status === 401
        ? 'Your session has expired — sign in again.'
        : `report download failed (${res.status})`
    );
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'report.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // a.click() reads the blob on a later task; revoking synchronously can abort the
  // save on Firefox/Safari, so defer it.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
};

// ── Wake-on-open ──────────────────────────────────────────────────────────────

export interface WakeResult {
  pending: { text: string; ui_blocks: A2uiPayload[] | null }[];
  greeting: string | null;
  ui_blocks: A2uiPayload[] | null;
}

export async function wakeOnOpen(
  screenContext: ScreenContext | null
): Promise<WakeResult | null> {
  try {
    const res = await fetchWithTimeout(`${gritClawBaseUrl()}/agui/wake`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(
        screenContext ? { state: { screen_context: screenContext } } : {}
      ),
    });
    if (!res.ok) return null;
    return (await res.json()) as WakeResult;
  } catch {
    return null;
  }
}

// ── User-scoped cron API ──────────────────────────────────────────────────────

export interface CronJob {
  id: number;
  name: string;
  cron: string;
  prompt: string;
  enabled: boolean;
  last_run_at: string | null;
  last_status: string | null;
  next_run_at: string | null;
}

const jsonOrThrow = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    let detail = `request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') detail = body.detail;
      else if (
        body?.detail &&
        typeof body.detail === 'object' &&
        typeof body.detail.message === 'string'
      ) {
        detail = body.detail.message;
      }
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
};

export const listJobs = async (): Promise<CronJob[]> =>
  jsonOrThrow<CronJob[]>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/jobs`, {
      headers: authHeader(),
    })
  );

export interface HistoryMessage {
  role: 'user' | 'assistant';
  text: string;
  corr_id?: string | null;
  /** Generative-UI catalog blocks the assistant turn emitted, re-attached by the
   *  gateway so cards survive a reload. Absent/null when the turn emitted none. */
  ui_blocks?: A2uiPayload[] | null;
}

export interface GritClawConversation {
  conversation_id: string;
  title: string | null;
  pinned: boolean;
  turn_count: number;
  created_at: string | null;
  last_active_at: string | null;
}

/** Status-aware failure for conversation list/history reads. These reads must
 *  distinguish an empty successful response from an unavailable endpoint, and
 *  callers use 404/410 specifically to prune stale conversation rows. */
export class GritClawRequestError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = 'GritClawRequestError';
    this.status = status;
  }
}

export const fetchConversations = async (): Promise<GritClawConversation[]> => {
  let res: Response;
  try {
    res = await fetchWithTimeout(`${gritClawBaseUrl()}/app/conversations`, {
      headers: authHeader(),
    });
  } catch {
    throw new GritClawRequestError('conversation list unavailable');
  }
  if (!res.ok) {
    // fetchWithTimeout already expires and redirects a rejected session. Keep
    // that terminal path quiet while making all other failures observable.
    if (sessionRejectedResponses.has(res)) return [];
    throw new GritClawRequestError('conversation list unavailable', res.status);
  }
  try {
    const body = (await res.json()) as {
      conversations?: GritClawConversation[];
    };
    return (body.conversations ?? []).map((c) => ({
      ...c,
      conversation_id: normalizeGritClawConversationId(c.conversation_id),
      title: c.title ?? null,
      pinned: Boolean(c.pinned),
      turn_count: Number(c.turn_count) || 0,
      created_at: c.created_at ?? null,
      last_active_at: c.last_active_at ?? null,
    }));
  } catch {
    throw new GritClawRequestError('conversation list unavailable', res.status);
  }
};

/** Rename and/or pin one of the user's conversations. Returns true on success. */
export const updateConversation = async (
  conversationId: string,
  patch: { title?: string; pinned?: boolean }
): Promise<boolean> => {
  try {
    const res = await fetchWithTimeout(
      `${gritClawBaseUrl()}/app/conversations/${encodeURIComponent(
        normalizeGritClawConversationId(conversationId)
      )}`,
      {
        method: 'PATCH',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }
    );
    return res.ok;
  } catch {
    return false;
  }
};

/** Permanently delete one of the user's conversations. A rejected promise is
 *  observable to callers so they never remove the local row on a failed DELETE. */
export const deleteConversation = async (
  conversationId: string
): Promise<void> => {
  const destructiveId = String(conversationId ?? '').trim();
  if (!destructiveId) {
    throw new GritClawRequestError('conversation id is required');
  }
  let res: Response;
  try {
    res = await fetchWithTimeout(
      `${gritClawBaseUrl()}/app/conversations/${encodeURIComponent(
        destructiveId
      )}`,
      {
        method: 'DELETE',
        headers: authHeader(),
      }
    );
  } catch {
    throw new GritClawRequestError('conversation deletion unavailable');
  }
  if (!res.ok) {
    throw new GritClawRequestError(
      'conversation deletion unavailable',
      res.status
    );
  }
};

/** Permanently delete every conversation owned by the authenticated user. */
export const deleteAllConversations = async (): Promise<void> => {
  let res: Response;
  try {
    res = await fetchWithTimeout(`${gritClawBaseUrl()}/app/conversations`, {
      method: 'DELETE',
      headers: authHeader(),
    });
  } catch {
    throw new GritClawRequestError('conversation deletion unavailable');
  }
  if (!res.ok) {
    throw new GritClawRequestError(
      'conversation deletion unavailable',
      res.status
    );
  }
};

export const fetchHistory = async (
  limit = 40,
  conversationId = getStoredGritClawConversationId()
): Promise<HistoryMessage[]> => {
  let res: Response;
  try {
    const params = new URLSearchParams({
      limit: String(limit),
      conversation_id: normalizeGritClawConversationId(conversationId),
    });
    res = await fetchWithTimeout(
      `${gritClawBaseUrl()}/app/history?${params.toString()}`,
      {
        headers: authHeader(),
      }
    );
  } catch {
    throw new GritClawRequestError('conversation history unavailable');
  }
  if (!res.ok) {
    if (sessionRejectedResponses.has(res)) return [];
    throw new GritClawRequestError(
      'conversation history unavailable',
      res.status
    );
  }
  try {
    const body = (await res.json()) as { messages?: HistoryMessage[] };
    return body.messages ?? [];
  } catch {
    throw new GritClawRequestError(
      'conversation history unavailable',
      res.status
    );
  }
};

export type PendingItem = WakeResult['pending'][number];

export const fetchInbox = async (): Promise<PendingItem[]> => {
  try {
    const res = await fetchWithTimeout(`${gritClawBaseUrl()}/app/inbox`, {
      headers: authHeader(),
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { pending?: PendingItem[] };
    return body.pending ?? [];
  } catch {
    return [];
  }
};

// ── Operator health (GET /app/status) ─────────────────────────────────────────

export interface GritClawStatus {
  mqtt_connected: boolean;
  last_heartbeat: { status: string | null; age_seconds: number | null };
  storm: {
    active: boolean;
    slots_used: number;
    cap: number;
    batched_depth: number;
  };
  adl_available: boolean;
  telemetry_db_reachable: boolean;
}

/** Operator-readiness subset (NOT the admin console). Null when GritClaw is
 *  unreachable — callers render an "unavailable" state rather than throw. */
export const fetchStatus = async (): Promise<GritClawStatus | null> => {
  try {
    const res = await fetchWithTimeout(`${gritClawBaseUrl()}/app/status`, {
      headers: authHeader(),
    });
    if (!res.ok) return null;
    return (await res.json()) as GritClawStatus;
  } catch {
    return null;
  }
};

export const createJob = async (job: {
  name: string;
  cron: string;
  prompt: string;
}): Promise<CronJob> =>
  jsonOrThrow<CronJob>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/jobs`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(job),
    })
  );

export const deleteJob = async (id: number): Promise<void> => {
  await jsonOrThrow<unknown>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/jobs/${id}`, {
      method: 'DELETE',
      headers: authHeader(),
    })
  );
};

export const runJob = async (id: number): Promise<void> => {
  await jsonOrThrow<unknown>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/jobs/${id}/run`, {
      method: 'POST',
      headers: authHeader(),
    })
  );
};

export const updateJob = async (
  id: number,
  patch: Partial<{ enabled: boolean; cron: string; prompt: string }>
): Promise<CronJob> =>
  jsonOrThrow<CronJob>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/jobs/${id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  );

// ── Web voice input (POST /app/transcribe) ────────────────────────────────────

/**
 * Send a MediaRecorder blob to the gateway, which runs the AwsTranscriber path
 * and returns the transcript. Multipart FormData — do NOT set Content-Type, the
 * browser sets the multipart boundary. Throws the gateway detail on 4xx/5xx
 * (e.g. 503 "transcription is unavailable" when AWS Transcribe isn't configured).
 */
export const transcribe = async (audio: Blob): Promise<string> => {
  const form = new FormData();
  form.append('audio', audio);
  const body = await jsonOrThrow<{ text: string }>(
    await fetchWithTimeout(
      `${gritClawBaseUrl()}/app/transcribe`,
      {
        method: 'POST',
        headers: authHeader(),
        body: form,
      },
      requestTimeoutMs(LONG_REQUEST_TIMEOUT_MS)
    )
  );
  return body.text;
};

// ── File attachments (POST /app/uploads) ─────────────────────────────────────

/** One file the user staged and we uploaded ahead of a turn. The gateway stores
 *  the bytes and returns an opaque id we forward as `forwardedProps.attachments`. */
export interface UploadedAttachment {
  upload_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

/** Machine-readable failure reason, mapped from the gateway status. The caller
 *  (provider) localizes it for the toast — the client stays i18n-free. */
export type AttachmentUploadErrorCode =
  | 'too_large' // 413 — over the size cap
  | 'unsupported_type' // 415 — type not allowed
  | 'unauthorized' // 401 — session expired
  | 'failed'; // 400 / 5xx / network

/** Typed upload failure carrying the offending filename and a stable code so the
 *  UI can explain it. `code` is duck-typed by consumers (safe across module mocks). */
export class AttachmentUploadError extends Error {
  code: AttachmentUploadErrorCode;
  filename: string;
  constructor(
    code: AttachmentUploadErrorCode,
    filename: string,
    message: string
  ) {
    super(message);
    this.name = 'AttachmentUploadError';
    this.code = code;
    this.filename = filename;
  }
}

const uploadErrorCode = (status: number): AttachmentUploadErrorCode => {
  if (status === 413) return 'too_large';
  if (status === 415) return 'unsupported_type';
  if (status === 401) return 'unauthorized';
  return 'failed';
};

/**
 * Upload one staged file to the gateway ahead of a turn. Multipart FormData under
 * field `file` — do NOT set Content-Type (the browser sets the multipart boundary),
 * same as `transcribe`. Throws {@link AttachmentUploadError} with a typed `code`
 * (413→too_large, 415→unsupported_type, 401→unauthorized, else failed) so the
 * caller can surface a filename-aware message; the server remains the authority.
 */
export const uploadAttachment = async (
  file: File
): Promise<UploadedAttachment> => {
  const form = new FormData();
  form.append('file', file);

  let res: Response;
  try {
    res = await fetchWithTimeout(
      `${gritClawBaseUrl()}/app/uploads`,
      {
        method: 'POST',
        headers: authHeader(),
        body: form,
      },
      requestTimeoutMs(LONG_REQUEST_TIMEOUT_MS)
    );
  } catch (err) {
    throw new AttachmentUploadError(
      'failed',
      file.name,
      err instanceof Error ? err.message : 'network error'
    );
  }

  if (!res.ok) {
    let detail = `upload failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* non-JSON error body — keep the status-based default */
    }
    throw new AttachmentUploadError(
      uploadErrorCode(res.status),
      file.name,
      detail
    );
  }

  return (await res.json()) as UploadedAttachment;
};

// ── Alert rules (/app/alert-rules) ────────────────────────────────────────────

/** A flat AND-matched condition (gateway rules.py ALLOWED_KEYS). All present keys
 *  must match. Numeric keys: battery_pct_lt, battery_voltage_lt, offline_minutes_gt. */
export interface AlertCondition {
  event_type?: string;
  device_id?: string;
  device_type?: string;
  depot?: string;
  severity?: string;
  battery_pct_lt?: number;
  battery_voltage_lt?: number;
  offline_minutes_gt?: number;
}

export interface AlertRule {
  id: number;
  name: string;
  condition: AlertCondition;
  deliver_to: string;
  /** Optional external recipients notified in addition to the GritCargo user. */
  email_recipients: string[];
  /** delivery urgency, distinct from condition.severity: info | warning | critical */
  severity: string;
  enabled: boolean;
  created_at: string | null;
}

export const listAlertRules = async (): Promise<AlertRule[]> =>
  jsonOrThrow<AlertRule[]>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/alert-rules`, {
      headers: authHeader(),
    })
  );

export const createAlertRule = async (rule: {
  name: string;
  condition: AlertCondition;
  email_recipients?: string[];
  severity?: string;
}): Promise<AlertRule> =>
  jsonOrThrow<AlertRule>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/alert-rules`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(rule),
    })
  );

export const updateAlertRule = async (
  id: number,
  patch: Partial<{
    enabled: boolean;
    name: string;
    condition: AlertCondition;
    /** An empty array clears all external email recipients. */
    email_recipients: string[] | null;
    severity: string;
  }>
): Promise<AlertRule> =>
  jsonOrThrow<AlertRule>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/alert-rules/${id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  );

export const deleteAlertRule = async (id: number): Promise<void> => {
  await jsonOrThrow<unknown>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/alert-rules/${id}`, {
      method: 'DELETE',
      headers: authHeader(),
    })
  );
};

// ── Saved report definitions (Reports page) ──────────────────────────────────
// A definition is config JSON the Reports page renders against live data with
// the viewer's own JWT. `shared` exposes it to the whole company; writes are
// owner-only (the gateway 404s anything else).

export interface ReportDefRecord {
  id: number;
  name: string;
  description: string | null;
  definition: unknown;
  shared: boolean;
  /** User ids (JWT subs) granted individual visibility. */
  shared_with: string[];
  mine: boolean;
  owner_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const listReportDefs = async (): Promise<ReportDefRecord[]> =>
  jsonOrThrow<ReportDefRecord[]>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/report-defs`, {
      headers: authHeader(),
    })
  );

export const getReportDef = async (id: number): Promise<ReportDefRecord> =>
  jsonOrThrow<ReportDefRecord>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/report-defs/${id}`, {
      headers: authHeader(),
    })
  );

export const createReportDef = async (payload: {
  name: string;
  description?: string;
  definition: unknown;
  shared?: boolean;
  shared_with?: string[];
}): Promise<ReportDefRecord> =>
  jsonOrThrow<ReportDefRecord>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/report-defs`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  );

export const updateReportDef = async (
  id: number,
  patch: Partial<{
    name: string;
    description: string | null;
    definition: unknown;
    shared: boolean;
    shared_with: string[];
  }>
): Promise<ReportDefRecord> =>
  jsonOrThrow<ReportDefRecord>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/report-defs/${id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  );

export const deleteReportDef = async (id: number): Promise<void> => {
  await jsonOrThrow<unknown>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/report-defs/${id}`, {
      method: 'DELETE',
      headers: authHeader(),
    })
  );
};

// ── Delivery preferences ──────────────────────────────────────────────────────

export interface DeliveryPreferences {
  sender_key: string;
  channels: string[];
  critical_bypasses_quiet_hours: boolean;
  quiet_hours: string | null; // "HH:MM-HH:MM" SAST, or null
  digest_time: string | null; // "HH:MM" SAST, or null
}

export const fetchPreferences = async (): Promise<DeliveryPreferences> =>
  jsonOrThrow<DeliveryPreferences>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/preferences`, {
      headers: authHeader(),
    })
  );

export const savePreferences = async (
  patch: Partial<{
    channels: string[];
    critical_bypasses_quiet_hours: boolean;
    quiet_hours: string | null;
    digest_time: string | null;
  }>
): Promise<DeliveryPreferences> =>
  jsonOrThrow<DeliveryPreferences>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/preferences`, {
      method: 'PUT',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  );

// ── Web push config (GET /app/web-push-config) ───────────────────────────────

export interface WebPushConfig {
  vapid_public_key: string;
}

/** Fetch the gateway's VAPID public key. Empty string means VAPID is not configured. */
export const getWebPushConfig = async (): Promise<WebPushConfig> =>
  jsonOrThrow<WebPushConfig>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/web-push-config`, {
      headers: authHeader(),
    })
  );

// ── Web push subscriptions (/app/web-push-subscriptions) ──────────────────────

export interface WebPushSubscriptionPayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export const registerWebPush = async (
  sub: WebPushSubscriptionPayload
): Promise<{ status: string; sender_key: string }> =>
  jsonOrThrow<{ status: string; sender_key: string }>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/web-push-subscriptions`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(sub),
    })
  );

export const deleteWebPush = async (endpoint: string): Promise<void> => {
  await jsonOrThrow<unknown>(
    await fetchWithTimeout(`${gritClawBaseUrl()}/app/web-push-subscriptions`, {
      method: 'DELETE',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    })
  );
};
